import {
    styled,
    outlinedInputClasses,
    formHelperTextClasses,
    inputLabelClasses,
    Checkbox,
    CheckboxProps,
    Box,
} from "@mui/material";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Typography, { TypographyProps } from "@mui/material/Typography";
import React, { FormEvent } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-light-svg-icons/faCheck";
import { PatternFormat } from "react-number-format";
import { NumberFormattingType } from "CommonTypes";

interface CustomNumberProps {
    onChange: (event: { target: { name: string; value: string } }) => void;
    name: string;
    value: string;
    type: NumberFormattingType;
    formattedValue: string;
}

export const filterInput = (r: RegExp) => (e: FormEvent<HTMLDivElement>) => {
    const target = e.target as any;
    target["value"] = target["value"].replace(r, "");
};

export const customNumberFormat = React.forwardRef<any, CustomNumberProps>(function CustomNumberFormat(props, ref) {
    const { onChange, value, type, ...other } = props;

    return (
        <PatternFormat
            {...other}
            value={value}
            getInputRef={ref}
            valueIsNumericString={true}
            displayType={"input"}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value,
                    },
                });
            }}
            format={getNumberFormatFromValueAndType(props.value, props.type)}
        />
    );
});

export const getNumberFormatFromValueAndType = (value: string, type: NumberFormattingType) => {
    switch (type) {
        case NumberFormattingType.phoneNumber: {
            // 4, 3, 3
            if (value.startsWith("04") || value.startsWith("05")) {
                return "#### ### ###";
            }
            // 2, 4, 4
            else if (
                value.startsWith("02") ||
                value.startsWith("03") ||
                value.startsWith("07") ||
                value.startsWith("08")
            ) {
                return "## #### ####";
            }
            // Default for phone numbers limit to 10
            return "##########";
        }
        case NumberFormattingType.creditCard: {
            // If other credit card formats need to switch here same as with phoneNumbers i.e. American Express 4 6 5 starting with 34**
            return "#### #### #### ####";
        }
    }
};

export const alphaSpaceApostropheHyphensOnly = filterInput(/[^a-zA-Z '-]/g);
export const digitsOnly = filterInput(/[^0-9]/g);

const checkBoxStyle = {
    width: "32px",
    height: "32px",
    backgroundColor: "#fff",
    border: "2px solid",
    color: "primary.main",
    borderRadius: "3px",
    fontSize: "21px",
    lineHeight: "34px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
};

export const RoadsideCheckbox: React.FC<CheckboxProps & { error?: boolean }> = ({ error, ...props }) => {
    return (
        <Checkbox
            icon={
                <Box
                    sx={{
                        ...checkBoxStyle,
                        borderColor: error ? "error.main" : "offGrey",
                    }}
                />
            }
            checkedIcon={
                <Box
                    sx={{
                        ...checkBoxStyle,
                        borderColor: error ? "error.main" : "primary.main",
                    }}
                >
                    <FontAwesomeIcon icon={faCheck} />
                </Box>
            }
            {...props}
        />
    );
};

export const RoadsideTextField = styled(TextField)<TextFieldProps>(({ theme }) => ({
    marginBottom: theme.spacing(3),
    [`& .${outlinedInputClasses.root}`]: {
        fontSize: 18,
        "& fieldset": {
            borderWidth: 2,
            borderColor: theme.palette.offGrey,
            "& legend": {
                color: theme.palette.offGrey,
            },
        },
        minWidth: "60px",
    },
    [`& .${inputLabelClasses.root}`]: {
        [`&.Mui-error`]: {
            color: theme.palette.error.main,
            opacity: 1,
        },
        [`&.${inputLabelClasses.shrink}`]: {
            opacity: 1,
        },
        color: theme.palette.offGrey,
        fontSize: 18,
        lineHeight: "26px",
    },
    [`& .${formHelperTextClasses.root}`]: {
        fontSize: 12,
        margin: 0,
        marginTop: "5px",
        lineHeight: "16px",
    },
    "& ::placeholder": {
        color: theme.palette.offGrey,
        opacity: 1,
        fontSize: 18,
        lineHeight: "26px",
    },
}));

export const RoadsideFieldLabel = styled(Typography)<TypographyProps>(({ theme }) => ({
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    fontWeight: 400,
    fontSize: 18,
    lineHeight: "26px",
}));
